import { useOutletContext, Link } from "react-router-dom";
import { useState, useEffect, useRef } from 'react';
import { CopyBlock, CodeBlock, monokai } from "react-code-blocks";
import { PAGE_TITLE_BASE, PageDisplayNames, PageNames } from '../../Assets/variables';
import { privateKeyCommand, certificateCommend } from "../../Assets/documentationExamples";
import { hrefNavigationOne, hrefNavigationTwo, hrefNavigationThree, postmanOne, pythonOne, pythonTwo, hrefNavigationFive } from "../../Assets/bulkApiDocumentationExamples";
import Images from "../../Assets/Images/images";
import InnerNavigation from "../../Components/InnerNavigation";
import { goToSection } from "../../Utils/pageNavigationHelpers";
import { Helmet } from "react-helmet-async";

const DocumentationSections = {
    GettingStarted: "getting-started",
    Introduction: "introduction",
    CreatingACertificateToUseTheBulkAPI: "creating-a-certificate-to-use-the-bulk-api",
    DownloadAFullExtractOfData: "download-a-full-extract-of-data",
    DownloadInitialLoads: "download-initial-loads",
    DownloadDeltas: "download-deltas",
    CallTheBulkAPIUsingPostman: "call-the-bulk-api-using-postman",
    CallTheBulkAPIUsingPython: "call-the-bulk-api-using-python",
    APILimits: "api-limits",
};

function downloadPDF() {
    var hiddenElement = document.createElement('a');
    hiddenElement.href = `${process.env.PUBLIC_URL}/Certificate_Portal_User_Guide.pdf`;
    hiddenElement.target = '_blank';
    hiddenElement.rel = 'noopener noreferrer';
    hiddenElement.click();
}

const BulkAPIDocumentation = () => {
    const {setActivePageItem} = useOutletContext();
    const [isShown, setIsShown] = useState({
        "getting-started": true
    });

    const gettingStartedRef = useRef(null);
    const introductionRef = useRef(null);
    const creatingACertificateToUseTheBulkAPIRef = useRef(null);
    const downloadAFullExtractOfDataRef = useRef(null);
    const downloadInitialLoadsRef = useRef(null);
    const downloadDeltasRef = useRef(null);
    const callTheBulkAPIUsingPostmanRef = useRef(null);
    const callTheBulkAPIUsingPythonRef = useRef(null);
    const apiLimitsRef = useRef(null);

    const headingRefs = {
        "getting-started": gettingStartedRef,
        "introduction": introductionRef,
        "creating-a-certificate-to-use-the-bulk-api": creatingACertificateToUseTheBulkAPIRef,
        "download-a-full-extract-of-data": downloadAFullExtractOfDataRef,
        "download-initial-loads": downloadInitialLoadsRef,
        "download-deltas": downloadDeltasRef,
        "call-the-bulk-api-using-postman": callTheBulkAPIUsingPostmanRef,
        "call-the-bulk-api-using-python": callTheBulkAPIUsingPythonRef,
        "api-limits": apiLimitsRef
    }
    
    useEffect(() => {
        setActivePageItem(PageNames.BulkAPIDocumentation);
        const currentUrl = window.location.href;
        const section = currentUrl.split("#")[1];
        if (section) {
            goToSection(headingRefs[section])
        }
    }, []);

    return (
        <>
            <Helmet>
                <title>{`${PAGE_TITLE_BASE}${PageDisplayNames.BulkAPIDocumentation}`}</title>
                <meta name="description" content={`${PageDisplayNames.BulkAPIDocumentation} page of the SIX Web API Portal`} />
                <link rel="canonical" href={`/${PageNames.BulkAPIDocumentation}`} />
            </Helmet>
            <div className="documentation">
                <div className="documentation-container">
                    <div className="inner-navigation">
                        <InnerNavigation isShown={isShown} setIsShown={setIsShown} headingRefs={headingRefs} goToSection={goToSection} />
                    </div>
                    <div id="documentation-content">
                        <h1>Documentation</h1>
                        <h2 id={DocumentationSections.GettingStarted} ref={gettingStartedRef}>Getting Started</h2>
                        <h3 className="hidden" id={DocumentationSections.GettingStarted}>Getting Started</h3>

                        <section>
                            <h3 id={DocumentationSections.Introduction} ref={introductionRef}>Introduction</h3>

                            <p>The Bulk API is designed to populate your database with millions of instruments or corporate actions details within minutes, rather than hours. You can request both Initial Loads and Updates at your convenience. Updates include all changes that have occurred since the specified date and time, ensuring your database remains current and responsive. The Bulk API is user-friendly, utilizing industry-standard technologies such as REST/JSON, and is supported by an intuitive data model to reduce development costs.</p>
                            <p>The following instructions will guide you through the process of creating an MTLS certificate for your user (a prerequisite for using the Bulk API), downloading a full extract and deltas of your datasets using the Bulk API, and finally, calling the Bulk API from POSTMAN or Python.</p>
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.CreatingACertificateToUseTheBulkAPI} ref={creatingACertificateToUseTheBulkAPIRef}>Creating a Certificate to Use the Bulk API</h3>

                            <p>The Bulk API uses MTLS certificate for secure authentication. This means that you will need a certificate before you can use the Bulk API. Below are the steps to create a valid certificate, have it signed and registered.</p>

                            <h4><strong>Step 1: Create a Certificate Signing Request (CSR)</strong></h4>
                            <p>Prerequisite: You need a SIX account before creating the CSR.</p>
                            <p>To create a CSR, follow these steps based on your operating system:</p>
                            <p><strong>UNIX / Mac:</strong> Use the commands below in your terminal:</p>

                            <p>Create a private key using the below command</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={privateKeyCommand}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>Replace <strong>full-user-identifier</strong> with your identifier (e.g., CH123-sixapiaccount), then create the CSR:</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={certificateCommend}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p><strong>Windows:</strong> You need OpenSSL or Cygwin installed in order to create the CSR in Windows. The commands are the same as for UNIX / Mac.</p>
                            <p>Here is the link to download <Link to="https://www.cygwin.com/" target="_blank">Cygwin</Link></p>
                            <p>Here is the link to download <Link to="https://wiki.openssl.org/index.php/Binaries" target="_blank">OpenSSL</Link></p>

                            <h4><strong>Step 2: Sign & Register the Certificate</strong></h4>
                            <p>Once you've created your CSR, it needs to be signed. This can be done by a Certificate Authority (CA) of your choice or by SIX.</p>
                            <p>You can use the SIX portal to sign and register your certificates. If you have a signed certificate, you can register the certificates from the SIX portal.</p>

                            <ol>
                                <li>Log in to SIX portal at <Link to="https://portal.six-group.com/" target="_blank">https://portal.six-group.com</Link></li>
                                <li>Follow our <a onClick={ () => {downloadPDF()} }>Certificate Portal User Guide</a> to sign and register your certificates</li>
                            </ol>

                            <p>Once registered your certificate status should be active and you can download the signed-certificate in the menu as illustrated below:</p>
                            <img src={Images.Authentication1} alt="Download the signed certificate" />

                            <p>Once the file is downloaded, it is recommended to rename it to signed-certificate.pem, so the file will have the same name as the examples provided in the document. </p>

                            <h4><strong>Step 3: Request for Entitlements</strong></h4>
                            <p>Additionally, the relevant permissions should be assigned to your user account to start using the APIs.</p>
                            <p>Please contact the SIX Customer Administration team to get the required permissions assigned to your account. They will guide you through the process and ensure your account is set up correctly.</p>
                            <p>With the Signed Certificate which you downloaded from Certificate Portal, you can now use your Signed Certificate and Private Key to authenticate yourself and start using the Bulk API.</p>
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.DownloadAFullExtractOfData} ref={downloadAFullExtractOfDataRef}>Download a Full Extract of Data</h3>

                            <p>Here are instructions to use the Bulk API to download the latest full extract / snapshot of SIX Data of your choice. This full extract can be downloaded for purposes such as performing an initial load.</p>

                            <p>In the below instructions, we query the SIX Extended Fixed Income Reference Data endpoint, which has the following root url <code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome</code>.</p>

                            <p>You can find details about other content sets available in the Bulk API in this API Portal Catalog (see left hand side menu).</p>
                            
                            <h4><strong>Step 1. Call the Bulk API and retrieve a snapshot ID</strong></h4>
        
                            <p>To download a full extract, start by appending <code>/snapshot</code> to the root url: <code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot</code>, which retrieves a list of snapshot IDs (the long IDs in the href fields):</p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={hrefNavigationOne}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p>You can choose to download the latest snapshot, identified using the <code>lastUpdated</code> field, which is the date when the whole Bulk API data of your choice was snapped into files, ready to be downloaded by Bulk API users.</p>
                            <p>Note that you may want to store the <code>lastUpdated</code> date of the snapshot of your choice as it will be used as the starting date when you want to download data updated since the snapshot (see Download Deltas instructions).</p>
        
                            <h4><strong>Step 2. Download the data from the selected snapshot</strong></h4>
                            <p>After selecting a snapshot, you will need to make multiple Bulk API queries in order to retrieve all the data in the snapshot.</p>
                            <p>The first query will be constructed by appending the <code>href</code> value of the snapshot which you selected above to the root url: <code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot/MjAyNC0xMC0yOFQxMjoyMTowMi44NTZa</code>, which retrieves:</p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={hrefNavigationTwo}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p>For each <code>href</code> in the response, append its value to the root url to construct the next URLs to query eg <code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot/MjAyNC0xMC0yOFQxMjoyMTowMi44NTZa/CG</code> and so on recursively until a Bulk API response doesn't include any <code>href</code> value, which means that it includes data rather than navigation information.</p>
                            <div className="code-example">
                                <CodeBlock
                                    text={hrefNavigationThree}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
                            <p>This the data which you want download, and for instance add to your database.</p>
                            <p><strong>Summary:</strong> by building all the queries following the above href based logic, this will allow you to download all the data available in the snapshot.</p>
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.DownloadDeltas} ref={downloadDeltasRef}>Download Deltas</h3>

                            <p>Here are instructions to retrieve the data updated either since the snapshot of your choice, then to monitor data updates.</p>
                            <h4><strong>Call the Bulk API to download updated SIX Data since the snapshot of your choice</strong></h4>
                            <p>In order to retrieve data updated during a time period, you should query the root url appended with <code>/update</code> and add <code>from</code> and <code>to</code> parameters using ISO time format.</p>
                            <p>For instance, to query updates between the time of the snapshot of your choice and 2 hours later, set the <code>from</code> parameter to the snapshot's <code>lastUpdated</code> value which you stored previously and the <code>to</code> parameter to <code>lastUpdated</code> + 2 hours: <code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/update?from=2024-10-28T07:12:30.153Z&to=2024-10-28T09:12:30.153Z</code> which retrieves</p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={hrefNavigationFive}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p>Then use the "href" based method to build the queries to download all the updated data, in the same way as when you downloaded a snapshot.</p>
                            <h4><strong>Call the Bulk API to monitor data updates</strong></h4>
                            <p>You can then use the <code>from</code> and <code>to</code> logic, with the <code>to</code> parameter from the previous query now used as <code>from</code> parameter in order to check for updates at the frequency of your choice. (see data catalog for details of data refresh frequency for the content of your choice)</p>
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.CallTheBulkAPIUsingPostman} ref={callTheBulkAPIUsingPostmanRef}>Call the Bulk API Using Postman</h3>

                            <p>Postman provides an easy to use interface to test the APIs. Here are some steps to call the Bulk API from Postman.</p>
                            <p><strong>Step 1.</strong> Download and install the Postman desktop agent - <Link to="https://www.postman.com/downloads/" target="_blank">Download Postman | Get Started for Free</Link></p>
                            <p><strong>Step 2.</strong> Add Certificates to Postman</p>
                            <p>The following steps describe how to set up certificates in Postman in order to query the Bulk API using MTLS Certificates. You should use the certificate which you created in <Link to="#creating-a-certificate-to-use-the-bulk-api">Creating a Certificate to Use the Bulk API</Link></p>
                            
                            <p>1. Open Postman's settings, top right of the app:</p>
                            <img src={Images.Postman1} alt="Open Postman's settings, top right of the app" />

                            <p>2. Open the Certificates tab and click on the Add Certificate link.</p>
                            <img src={Images.Postman2} alt="Open the Certificates tab and click on the Add Certificate link" />

                            <p>Enter the host name as api.six-group.com click on Select File CRT file</p> 
                            <p>Click on Select file next to CRT file and select the signed certificate</p>  
                            <p>Click on Select file next KEY file and select the private key and click Add</p>
                            <img src={Images.Postman3} alt="Then enter the Bulk API url" />

                            <p><strong>Step 3.</strong> Make the request</p>
                            <img src={Images.Postman4} alt="Make the request" />

                            <p>Add the request url next to GET:</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={postmanOne}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
                            <img src={Images.Postman5Bulk} alt="Add the request url next to GET" />

                            <p>Click Send to see the response</p>
                            <img src={Images.Postman6Bulk} alt="Click Send to see the response" />
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.CallTheBulkAPIUsingPython} ref={callTheBulkAPIUsingPythonRef}>Call the Bulk API Using Python</h3>

                            <p>The following Python Code includes simple classes for calling the Bulk API Corporate Actions end point in a multithreaded solution, then display its number of records in the latest snapshot and in the updates since. </p>
                            <p><strong>Important:</strong> It will sometimes happen that fields will be added to the schema without notice given to clients. Therefore, when calling the Bulk API from an application written in a programming language such as Python or JAVA, please make sure that your application won't break if new fields are introduced or fields which you are not using are removed.</p>
                            <p>For optimal performance, the Bulk API below should be integrated in a multi-threaded solution implemented in python, Java, etc.</p>
                            <p>Before using this code, look for the word 'Action:' and edit accordingly.</p>

                            <div className="code-example">
                                <CopyBlock
                                    text={pythonOne}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <div className="code-example">
                                <CopyBlock
                                    text={pythonTwo}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.APILimits} ref={apiLimitsRef}>API Limits</h3>
                            <p><strong>Rate Limits</strong></p>
                            <ul>
                                <li>400 requests per minute</li>
                                <li>200,000 requests per day</li>
                            </ul>

                            <p><strong>Query Timeouts</strong></p>
                            <p>Bulk API queries time out after 30 seconds.</p>

                            <p><strong>Retries</strong></p>
                            <p>Bulk API requests may fail due to transient issues such as network interruptions or temporary service unavailability.</p>
                            <p>To improve reliability, implement retry logic with appropriate backoff and error handling.</p>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BulkAPIDocumentation;